import React, { useState, useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, doc, addDoc, onSnapshot } from 'firebase/firestore';
import { app, db } from '../../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';

import {
  Check,
  ArrowRight,
  ArrowLeft,
  Lock,
  TrendingUp
} from 'lucide-react';

// Simplified pricing - single global pricing structure (TEMPORARILY HIDDEN)
const PRICES = {
  monthly: { id: 'price_1QbB4pHHaluHTLHlU3zl3R4i', currency: 'USD', amount: 0.00 },
  yearly: { id: 'price_1RF5EVHHaluHTLHlvJTpJZzL', currency: 'USD', amount: 0.00 }
};

// Single English-only content - globally consistent
const content = {
  title: "PRO ANALYTICS",
  subtitle: "Advanced sports data analysis platform",
  planTitle: "PRO TIER",
  features: [
    "Real-time sports API data access",
    "AI-powered predictive analytics",
    "Custom research on available leagues",
    "14× query limit increase",
    "Up to 50% discount on StarCoins packages",
    "Priority support channel"
  ],
  subscribe: "COMING SOON",
  loginRequired: "Authentication required",
  processing: "Processing",
  perMonth: "/mo",
  perYear: "/yr",
  yearly: "ANNUAL",
  monthly: "MONTHLY",
  savePercent: "−17%",
  bestValue: "OPTIMAL",
  stats: "ENHANCED ACCESS",
  guarantee: "30-day guarantee",
  securePayment: "Secured by Stripe",
  disclaimer: "Analytics for entertainment and research purposes only."
};

const PremiumPricing = () => {
  // Billing cycle state
  const [cycle, setCycle] = useState('monthly');
  
  // Auth and checkout states
  const auth = getAuth(app);
  const currentUser = auth.currentUser;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Format price - simplified to USD only
  const { id: priceId, currency, amount } = useMemo(() => PRICES[cycle], [cycle]);
  
  const formatter = useMemo(
    () => new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 2 
    }),
    []
  );
  
  const priceFormatted = formatter.format(amount);
  
  // STRIPE CHECKOUT FUNCTION
  const handleSubscribe = async () => {
    if (!currentUser) {
      setError(content.loginRequired);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create customer reference
      const customerRef = doc(db, 'customers', currentUser.uid);
      const checkoutSessionRef = collection(customerRef, 'checkout_sessions');
      
      console.log('Creating session with price:', priceId);
      const docRef = await addDoc(checkoutSessionRef, {
        line_items: [{ price: priceId, quantity: 1 }],
        success_url: window.location.origin + '/dashboard',
        cancel_url: window.location.origin + '/pricing',
        mode: 'subscription',
        customer_email: currentUser.email,
        allow_promotion_codes: true,
        collect_shipping_address: false,
        metadata: { 
          userId: currentUser.uid, 
          userEmail: currentUser.email,
          billingCycle: cycle 
        }
      });
      
      // Listen for changes to the checkout session
      onSnapshot(docRef, (snap) => {
        const data = snap.data();
        if (data?.error) {
          setError(data.error.message || 'Checkout session error');
          setLoading(false);
        } else if (data?.url) {
          // Redirect to Stripe Checkout
          window.location.assign(data.url);
        }
      });
    } catch (err) {
      setError(err.message || 'Failed to start subscription');
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-black relative">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />


      <main className="relative z-10 max-w-screen-xl mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Back Button */}
          <div className="mb-8">
            <Link
              to="/home"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-all duration-300 text-sm font-medium group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
              <span>Back</span>
            </Link>
          </div>

          {/* Terminal-style section header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 pb-6 border-b border-white/[0.05]"
          >
            <h1 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-4">
              SUBSCRIPTION TIER
            </h1>
            <h2 className="text-2xl text-white/95 mb-2">
              {content.title}
            </h2>
            <p className="text-sm text-white/50">
              {content.subtitle}
            </p>
          </motion.div>

          {/* Billing cycle toggle - Terminal style */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <div className="bg-white/[0.02] backdrop-blur-sm rounded-md p-1 flex border border-white/[0.05]">
              {['monthly', 'yearly'].map((option) => (
                <button
                  key={option}
                  onClick={() => setCycle(option)}
                  className={`relative flex-1 py-3 px-6 rounded text-center transition-all duration-200 ${
                    cycle === option
                      ? 'bg-white/[0.05] text-white/80 border border-emerald-500/20'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/[0.02]'
                  }`}
                >
                  <span className="text-xs font-medium tracking-[0.15em] uppercase">
                    {option === 'monthly' ? content.monthly : content.yearly}
                  </span>

                  {option === 'yearly' && (
                    <div className="absolute -top-2 right-4 bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30 text-emerald-400/80 text-[10px] font-mono px-2 py-0.5 rounded">
                      {content.savePercent}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Glass Data Card - Pricing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full mb-6"
          >
            {/* Card with glass effect */}
            <div className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg overflow-hidden hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
              {/* Live indicator */}
              <div className="absolute top-4 right-4">
                <span className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse" />
              </div>

              {/* Header section */}
              <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border-b border-white/[0.05] px-6 py-4">
                <h2 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase mb-2">
                  {content.planTitle}
                </h2>
                <div className="flex items-baseline">
                  <span className="font-mono text-xs text-emerald-400/80 uppercase tracking-wider">
                    {content.bestValue}
                  </span>
                  <span className="mx-2 text-white/10">•</span>
                  <span className="text-[11px] text-white/30">
                    {content.stats}
                  </span>
                </div>
              </div>
            
              {/* Price display */}
              <div className="px-6 py-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-baseline">
                    <span className="font-mono text-4xl text-white/95">
                      $x.xx
                    </span>
                    <span className="font-mono text-sm text-white/50 ml-2">
                      {cycle === 'monthly' ? content.perMonth : content.perYear}
                    </span>
                  </div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mt-2">
                    {content.guarantee}
                  </p>
                </div>
              
              {/* Error message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-red-500/10 border border-red-500/40 text-red-300 p-4 rounded-xl mb-4 text-sm"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>
              
                {/* Primary CTA - Disabled for Coming Soon */}
                <button
                  disabled={true}
                  className="w-full relative px-8 py-3 rounded-md text-white/60 text-[11px] font-medium tracking-[0.15em] uppercase transition-all duration-200 opacity-70 cursor-not-allowed bg-gradient-to-r from-emerald-500/30 to-violet-500/30"
                >
                  <span className="flex items-center justify-center">
                    {content.subscribe}
                  </span>
                </button>
              
                {/* Login required message */}
                {!currentUser && (
                  <div className="flex justify-center items-center mt-4">
                    <Lock className="w-3 h-3 text-white/30 mr-1" />
                    <p className="text-[11px] text-white/30 uppercase tracking-wider">
                      {content.loginRequired}
                    </p>
                  </div>
                )}

                {/* Secure payment message */}
                <div className="flex justify-center items-center mt-4">
                  <Lock className="w-3 h-3 text-white/30 mr-1" />
                  <p className="text-[10px] text-white/30 uppercase tracking-wider">
                    {content.securePayment}
                  </p>
                </div>
              </div>
            
              {/* Features section */}
              <div className="border-t border-white/[0.05] px-6 py-4">
                <h3 className="text-[10px] font-medium tracking-[0.2em] text-white/30 uppercase mb-4">
                  Features Included
                </h3>

                <ul className="space-y-3">
                  {content.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + (idx * 0.05) }}
                      className="flex items-start group"
                    >
                      <div className="flex-shrink-0 w-4 h-4 mt-0.5 rounded bg-emerald-500/[0.15] flex items-center justify-center">
                        <Check size={10} className="text-emerald-400/80" />
                      </div>
                      <span className="ml-3 text-sm text-white/70 group-hover:text-white/80 transition-colors">
                        {feature}
                      </span>
                    </motion.li>
                  ))}
                </ul>

                <p className="text-[10px] text-white/30 mt-6 uppercase tracking-wider">
                  {content.disclaimer}
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Info card with glass effect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 mb-8 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200"
          >
            <div className="flex items-center mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-400/80 mr-2" />
              <span className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">
                Platform Benefits
              </span>
            </div>
            <p className="text-sm text-white/50 leading-relaxed mb-4">
              Pro tier members receive 14× query allocation for our AI analytics engine, enabling comprehensive sports data analysis and predictive insights for enhanced research capabilities.
            </p>
            <p className="text-[11px] text-white/30 uppercase tracking-wider">
              — PICKSTAR AI ANALYTICS
            </p>
          </motion.div>
        </div>
      </main>
    </div>
    </>
  );
};

export default PremiumPricing;